import dayjs, { Dayjs } from 'dayjs';
import {
  NoScheduleReason,
  TIMEBOX_PREFS_ITEMS_INFO,
  WorkHourPrefItemState,
  TimeboxPrefItemState,
  RoutinePrefItemState,
  TasksPrefItemState,
} from '../../components';
import { defaultEventCategorySet, TimeboxSessionType, TimeFormatType } from '@demind-inc/core';
import { AgentMessageId, ScheduleValidationResult } from './types';
import {
  inferEndDayOffset,
  formatWorkHoursRange,
  translateMinToHHhMMmString,
  transformHHmmToTargetDate,
} from '../../data-access';

export const OVERDUE_TASK_RECENT_DAY_THRESHOLD = 7;
const deepWorkLabelName = defaultEventCategorySet.find(
  (item) => item.id === 'default_deepWork'
)?.name!;
const lightTaskLabelName = defaultEventCategorySet.find(
  (item) => item.id === 'default_lightTask'
)?.name!;

export const getTitleForDay = (date: Dayjs): string => {
  if (date.isSame(dayjs(), 'day')) {
    return 'today';
  } else if (date.isSame(dayjs().add(1, 'day'), 'day')) {
    return 'tomorrow';
  } else {
    return `on ${date.format('MMM D')}`;
  }
};

export const validateScheduleAvailability = (
  schedulerPreferences: any,
  selectedDate: Dayjs
): ScheduleValidationResult => {
  const now = dayjs();

  if (!schedulerPreferences.workTime?.end) {
    return { canSchedule: true };
  }

  if (selectedDate.isBefore(now, 'date')) {
    return { canSchedule: false, noScheduleReason: 'pastday' as NoScheduleReason };
  }

  if (selectedDate.isAfter(now, 'date')) {
    return { canSchedule: true };
  }

  const workStart = schedulerPreferences.workTime?.start;
  const workEnd = schedulerPreferences.workTime?.end;
  if (!workStart || !workEnd) {
    return { canSchedule: true };
  }

  const resolvedEndDayOffset = inferEndDayOffset(
    workStart,
    workEnd,
    schedulerPreferences.workTime?.endDayOffset
  );
  const targetDay = selectedDate.startOf('day');
  const windowEnd = dayjs(transformHHmmToTargetDate(workEnd, targetDay)).add(
    resolvedEndDayOffset ?? 0,
    'day'
  );
  const canSchedule = !now.isAfter(windowEnd);

  return canSchedule
    ? { canSchedule: true }
    : { canSchedule: false, noScheduleReason: 'afterWork' as NoScheduleReason };
};

export const getPreferenceSummary = (
  prefId: AgentMessageId,
  workHourPrefItem: WorkHourPrefItemState,
  timeBoxPrefItems: TimeboxPrefItemState,
  tasksPrefItems: TasksPrefItemState,
  routinePrefItem: RoutinePrefItemState,
  timeFormat: TimeFormatType
): string => {
  switch (prefId) {
    case 'tasks':
      const selectedTasks = Object.values(tasksPrefItems).filter((task) => task.selected);

      if (!selectedTasks.length) {
        return 'No tasks selected.';
      }

      const deepWorkTasks = selectedTasks.filter((task) =>
        task.labels?.some((label) => label.name.includes(deepWorkLabelName))
      );
      const lightTasks = selectedTasks.filter((task) =>
        task.labels?.some((label) => label.name.includes(lightTaskLabelName))
      );

      const deepWorkDurationStr = translateMinToHHhMMmString(
        deepWorkTasks.reduce((total: number, task: any) => {
          return total + (task.duration?.amount || 0);
        }, 0)
      );
      const lightTaskDurationStr = translateMinToHHhMMmString(
        lightTasks.reduce((total: number, task: any) => {
          return total + (task.duration?.amount || 0);
        }, 0)
      );
      return `Deep work tasks: ${deepWorkTasks.length} task${
        deepWorkTasks.length > 1 ? 's' : ''
      }, ${deepWorkDurationStr}.\nLight tasks: ${lightTasks.length} task${
        lightTasks.length > 1 ? 's' : ''
      }, ${lightTaskDurationStr}.`;

    case 'routine':
      const selectedRoutine = Object.values(routinePrefItem).filter(
        (activity) => activity.selected
      );
      if (selectedRoutine.length > 0) {
        const routineCount = selectedRoutine.length;
        const totalDuration = selectedRoutine.reduce((total: number, activity: any) => {
          return total + (activity.durationMin || 0);
        }, 0);
        const durationStr = translateMinToHHhMMmString(totalDuration);
        return `${routineCount} routine activit${routineCount > 1 ? 'ies' : 'y'} (${durationStr})`;
      } else {
        return 'No routine activities';
      }

    default:
      return '';
  }
};
